import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import countryService from '../services/countryService';
import pmmService from '../services/paymentMigrationManagerService';
import './AddCorporateProfilePage.css';

const AGENT_TYPES = [
    { value: 'OTA', label: 'OTA (Online Travel Agent)' },
    { value: 'COM', label: 'COM (Company)' },
    { value: 'SRC', label: 'SRC (Source)' },
    { value: 'TRA', label: 'TRA (Travel Agent)' },
];

const EMPTY_FORM = { agentType: 'COM', profileId: '', name: '', address: '', city: '', country: '' };

const PMMAddCorporateProfilePage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        countryService.getAllCountries().then(res => setCountries(res.data?.countries || [])).catch(() => {});
    }, []);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.agentType) e.agentType = 'Agent Type is required';
        if (!form.profileId.trim()) e.profileId = 'Profile ID is required';
        if (!form.name.trim()) e.name = 'Name is required';
        return e;
    };

    const handleCreate = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await pmmService.createCorporateProfile(id, {
                agent_type: form.agentType,
                profile_id: form.profileId,
                name:       form.name,
                address:    form.address || null,
                city:       form.city    || null,
                country:    form.country || null,
                active:     true,
            });
            navigate(`/apps/payment-migration-manager/${id}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save profile' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">Add Corporate Profile</h1>
                    <p className="acp-subtitle">Add Opera Cloud Corporate Profile IDs for reservation filtering</p>
                </div>
                <div className="acp-divider" />
                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Agent Type <span className="acp-required">*</span></label>
                            <select className={`acp-select${errors.agentType ? ' acp-input-error' : ''}`} value={form.agentType} onChange={e => set('agentType', e.target.value)}>
                                {AGENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            {errors.agentType && <span className="acp-error-msg">{errors.agentType}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Opera Cloud Profile ID <span className="acp-required">*</span></label>
                            <input className={`acp-input${errors.profileId ? ' acp-input-error' : ''}`} type="text" placeholder="e.g., 12345" value={form.profileId} onChange={e => set('profileId', e.target.value)} autoFocus />
                            {errors.profileId && <span className="acp-error-msg">{errors.profileId}</span>}
                        </div>
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Name <span className="acp-required">*</span></label>
                        <input className={`acp-input${errors.name ? ' acp-input-error' : ''}`} type="text" placeholder="e.g., Booking.com" value={form.name} onChange={e => set('name', e.target.value)} />
                        {errors.name && <span className="acp-error-msg">{errors.name}</span>}
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Address</label>
                        <input className="acp-input" type="text" placeholder="Enter address" value={form.address} onChange={e => set('address', e.target.value)} />
                    </div>
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">City</label>
                            <input className="acp-input" type="text" placeholder="Enter city" value={form.city} onChange={e => set('city', e.target.value)} />
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Country</label>
                            <select className="acp-select" value={form.country} onChange={e => set('country', e.target.value)}>
                                <option value="">Select country</option>
                                {countries.map(c => <option key={c.code} value={c.code}>{c.description}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(`/apps/payment-migration-manager/${id}`)} disabled={saving}>Cancel</button>
                    <button className="acp-btn-create" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
                </div>
            </div>
        </div>
    );
};

export default PMMAddCorporateProfilePage;
